import COMMON from 'constants/common';
import React, { useEffect, useState } from 'react';
import Layout from 'components/system/layouts/Layout';
import ScreenContext from 'Contexts/Common/ScreenContext';
import MachineConfContent from 'components/machineConf/organisms/contents/MachineConfContent';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ReturnButton } from 'components/system/atoms';
import NormalButton from 'components/system/atoms/buttons/NormalButton';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getClassificationName } from 'utils/common.helper';
import CLSFCN from 'constants/classification';
import redirectLinks from 'constants/redirectLinks';
import { useNavigate, useLocation } from 'react-router-dom';
import { handleCallSystemError } from 'redux/slices/commonSlice';
import ERROR_CODE from 'constants/errorCode';
import { SaveDetailInfoType } from 'types/machineConf/stationSelectType';
import UrlHelper from 'utils/url.helper';

/* ************ Context ************ */
const ScreenContextValue = {
  title: '共通',
  screenId: COMMON.SCREEN_ID.COMMON_CONF_DUO,
  currentMenu: COMMON.MENU.MACHINE_CONF,
  disableContents: true,
};

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    minWidth: '1080px',
    minHeight: '563px',
    margin: '18px 4px',
  },
  buttonArea: {
    display: 'grid',
    gap: '10px',
    width: '164px',
    height: '154px',
  },
  returnButton: {
    fontSize: '20px',
    lineHeight: '24px',
    width: '157px',
    height: '70px',
    zIndex: 200,
  },
  button: {
    width: '157px',
    height: '70px',
    background: '#EBEBEB',
    margin: '0 0 1px auto',
    fontSize: '20px',
    boxShadow: '5px 5px 3px rgba(0, 0, 0, 0.2)',
    textAlign: 'left',
    lineHeight: '24px',
    justifyContent: 'flex-start',
    padding: '6px 8px',
  },

  mainArea: {
    width: '1000px',
    gap: '10px',
  },
  description: {
    fontSize: '23px',
    padding: '34px 94px',
    height: '94px',
  },
  middleContent: {
    display: 'flex',
    margin: '0 10px 0 57px',
    width: '804px',
    height: '94px',
  },
  table: {
    width: '100%',
    '& th,td': {
      border: '1px solid #707070',
      fontSize: '23px',
      minHeight: '74px',
    },
    '& th': {
      textAlign: 'center',
      padding: 0,
    },
    '& tbody tr': {},
    '& td': {
      padding: '1px 6px',
      '& .cellStyle': {
        fontSize: '16px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        marginTop: 15,
      },
    },
    '& td.bgGray': {
      backgroundColor: '#EEEEEE',
    },
  },
}));

/* ******** Constants ******** */

/* ******** Main Component ******** */
/**
 *
 * @returns {React.FC} - 照明演出設定
 *
 */
const CommonConfDuo: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [savedConfProps] = useState(
    location.state !== undefined && location.state !== null ? (location.state as SaveDetailInfoType) : null,
  );
  const dispatch = useAppDispatch();

  /* ************ State ************ */
  // Global
  const partitionOtherConfDuo = useAppSelector((state) => state.machineConf.machineDuoConf.partitionOtherConfDuo);
  const { selectedStationList } = useAppSelector((state) => state.machineConf);
  const { confId: selfConfId } = selectedStationList[0];

  /* ************ Event ************ */
  // 画面表示時
  useEffect(() => {
    // グローバルステートが見つからない場合、システムエラー画面に遷移
    if (!partitionOtherConfDuo) {
      dispatch(handleCallSystemError({ errorNo: ERROR_CODE.NOT_FOUND }));
    }
  }, []);

  // 戻るボタン
  const handleClickReturn = () => {
    navigate(
      UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_DUO_HOME_DETAIL, {
        selectConfId: savedConfProps ? 'none' : selfConfId.toString(),
      }),
    );
  };
  // サウンド/照明ボタン
  const handleClickLedSound = () => {
    navigate(redirectLinks.MCONF_DUO_CONF_LED_SOUND);
  };

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <MachineConfContent savedConfProps={savedConfProps}>
          <Box className={classes.root}>
            <Box className={classes.buttonArea}>
              <ReturnButton className={classes.returnButton} onClick={handleClickReturn}>
                機器設定
                <br /> 詳細
              </ReturnButton>
              <NormalButton className={classes.button} onClick={handleClickLedSound}>
                サウンド/照明
              </NormalButton>
            </Box>

            <Box className={classes.mainArea}>
              <Box className={classes.description}>「共通」の設定内容です。</Box>
              <Box className={classes.middleContent}>
                <Table className={classes.table} aria-label="table">
                  <TableHead
                    sx={{
                      backgroundColor: '#B3B3B3',
                      '& .MuiTableCell-head': {
                        fontSize: '19px',
                      },
                    }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: '402px',
                        }}>
                        設定項目
                      </TableCell>
                      <TableCell
                        colSpan={2}
                        sx={{
                          width: '402px',
                        }}>
                        内容
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody
                    sx={{
                      '& .MuiTableCell-root:not(.bgGray)': {
                        backgroundColor: '#FFFFFF',
                      },
                      '& .MuiTableCell-body': {
                        fontSize: '15px',
                      },
                    }}>
                    <TableRow>
                      <TableCell className="bgGray" sx={{}}>
                        両替キープ
                      </TableCell>

                      <TableCell sx={{ width: '86px' }}>
                        {partitionOtherConfDuo?.cmnKeepTime &&
                          getClassificationName(CLSFCN.DUO_KEEP_TIME.CATEGORY_CD, partitionOtherConfDuo.cmnKeepTime)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell className="bgGray" sx={{}}>
                        イニシャル動作
                      </TableCell>
                      <TableCell sx={{}}>
                        {partitionOtherConfDuo?.cmnInitialOperation &&
                          getClassificationName(
                            CLSFCN.DUO_INITIAL_OPERATION.CATEGORY_CD,
                            partitionOtherConfDuo.cmnInitialOperation,
                          )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Box>
        </MachineConfContent>
      </Layout>
    </ScreenContext.Provider>
  );
};

export default CommonConfDuo;
